import { supabase } from '@/integrations/supabase/client';

const DEFAULT_SIGNED_URL_TTL_SECONDS = 60 * 60;
const SIGNED_URL_SKEW_SECONDS = 30;
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

function getCacheKey(bucket: string, path: string) {
  return `${bucket}:${path}`;
}

function getCachedUrl(bucket: string, path: string) {
  const key = getCacheKey(bucket, path);
  const cached = signedUrlCache.get(key);
  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) {
    signedUrlCache.delete(key);
    return null;
  }
  return cached.url;
}

function setCachedUrl(bucket: string, path: string, url: string, ttlSeconds: number) {
  const expiresAt = Date.now() + Math.max(ttlSeconds - SIGNED_URL_SKEW_SECONDS, 5) * 1000;
  signedUrlCache.set(getCacheKey(bucket, path), { url, expiresAt });
}

function isSupabaseStorageUrl(value: string) {
  return value.includes('/storage/v1/object/');
}

function isSignedStorageUrl(value: string) {
  return value.includes('/storage/v1/object/sign/');
}

export function extractStoragePath(value: string, bucket: string) {
  if (!value) return null;
  if (!value.startsWith('http')) return value.replace(/^\/+/, '');

  if (!isSupabaseStorageUrl(value)) return null;

  const matcher = new RegExp(`/storage/v1/object/(?:public|sign)/${bucket}/(.+?)(?:\\?|$)`);
  const match = value.match(matcher);
  if (!match || !match[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function needsSignedUrl(value: string, bucket: string) {
  if (!value) return false;
  if (!value.startsWith('http')) return true;
  if (!isSupabaseStorageUrl(value)) return false;
  if (isSignedStorageUrl(value)) return false;
  return value.includes(`/storage/v1/object/public/${bucket}/`);
}

export function getImmediateStorageUrl(value: string | null, bucket: string) {
  if (!value) return null;
  if (!needsSignedUrl(value, bucket)) return value;

  const path = extractStoragePath(value, bucket);
  if (!path) return value;

  const cached = getCachedUrl(bucket, path);
  if (cached) return cached;

  return null;
}

export async function resolveStorageUrl(
  value: string | null | undefined,
  bucket = 'post-media',
  ttlSeconds = DEFAULT_SIGNED_URL_TTL_SECONDS,
) {
  if (!value) return null;
  if (!needsSignedUrl(value, bucket)) return value;

  const path = extractStoragePath(value, bucket);
  if (!path) return value;

  const cached = getCachedUrl(bucket, path);
  if (cached) return cached;

  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, ttlSeconds);
  if (error || !data?.signedUrl) {
    console.warn('Failed to create signed URL', error);
    return value;
  }

  setCachedUrl(bucket, path, data.signedUrl, ttlSeconds);
  return data.signedUrl;
}

export async function resolveStorageUrls(
  values: Array<string | null | undefined>,
  bucket = 'post-media',
  ttlSeconds = DEFAULT_SIGNED_URL_TTL_SECONDS,
) {
  const uniqueValues = Array.from(new Set(values.filter(Boolean))) as string[];
  if (uniqueValues.length === 0) return new Map<string, string>();

  const entries = await Promise.all(
    uniqueValues.map(async (value) => {
      const resolved = await resolveStorageUrl(value, bucket, ttlSeconds);
      return [value, resolved || value] as const;
    }),
  );

  return new Map(entries);
}
